import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Users, BarChart, Key } from "lucide-react";
import CTASectionWrapper from "@/components/CTASectionWrapper";
import CTAButton from "@/components/CTAButton";
import { useTranslation } from "react-i18next";

const StoreAdvantagesSection = () => {
  const { t } = useTranslation();
  
  const advantages = [
    {
      icon: <CheckCircle className="h-6 w-6 text-anzir" />,
      title: t('cta.storeAdvantages.access', 'Access to Global Scripts'),
      description: t('cta.storeAdvantages.accessDesc', 'Get access to a vast library of curated mystery scripts from global creators.')
    },
    {
      icon: <Users className="h-6 w-6 text-anzir" />,
      title: t('cta.storeAdvantages.community', 'Community Support'),
      description: t('cta.storeAdvantages.communityDesc', 'Join a network of venue owners sharing best practices and experiences.')
    },
    {
      icon: <BarChart className="h-6 w-6 text-anzir" />,
      title: t('cta.storeAdvantages.analytics', 'Business Analytics'),
      description: t('cta.storeAdvantages.analyticsDesc', 'Track performance with intuitive analytics and reporting tools.')
    },
    {
      icon: <Key className="h-6 w-6 text-anzir" />,
      title: t('cta.storeAdvantages.tools', 'Digital Management Tools'),
      description: t('cta.storeAdvantages.toolsDesc', 'Simplify operations with booking, inventory and customer management tools.')
    }
  ];
  
  return (
    <CTASectionWrapper 
      className="py-20 bg-gradient-to-b from-black to-black/95"
      backgroundImage="/lovable-uploads/c119c9ee-af0f-4195-b8b7-0b3ecad8a4fb.webp"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 gap-12 md:gap-20">
          {/* Top Content: Header and Description */}
          <div className="text-center max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
            >
              {t('cta.storeAdvantages.title', 'Transform Your Venue Into a MysteryAct Hub')}
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg text-white/80 mb-8 max-w-3xl mx-auto"
            >
              {t('cta.storeAdvantages.description', 'Anzir provides everything you need to start or enhance your MysteryAct venue. With minimal investment and our comprehensive platform, you can create unforgettable experiences for your customers.')}
            </motion.p>
          </div>
          
          {/* Middle Content: Advantages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
                className="bg-black/70 rounded-xl p-6 backdrop-blur-sm border border-amber-900/30 transition-all duration-300 shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 bg-anzir/10 p-3 rounded-lg">
                    {advantage.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-white mb-2">{advantage.title}</h3>
                    <p className="text-white/70">{advantage.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Bottom Content: CTAs */}
          <div className="flex flex-col md:flex-row gap-6 justify-center items-center mt-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <CTAButton 
                to="/store"
                className="bg-anzir hover:bg-anzir/90 px-8 py-4 text-lg shadow-lg shadow-anzir/20"
              >
                {t('cta.storeAdvantages.getStarted', 'Start Your Venue Journey')}
              </CTAButton>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <CTAButton 
                to="/stores"
                className="bg-black border border-anzir hover:bg-black/70 px-8 py-4 text-lg hover:border-anzir/90"
              >
                {t('cta.storeAdvantages.explore', 'Explore Existing Venues')}
              </CTAButton>
            </motion.div>
          </div>
        </div>
      </div>
    </CTASectionWrapper>
  );
};

export default StoreAdvantagesSection;
